/*
 * Jira Ticket:
 * Created Date: Mon, 6th Apr 2020, 09:39:09 am
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { Fragment, useEffect, useState } from 'react';
import {
  fetchEnd,
  fetchStart,
  useDataProvider,
  useRefresh,
  useNotify,
  useTranslate,
} from 'react-admin';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { Done as TrueIcon, Clear as FalseIcon } from '@material-ui/icons';
import {
  ConfirmModalContent,
  RedirectModalContent,
  InformationModalContent,
  CancelSubscriptionModalContent,
} from './ModalContent';
import { ConfirmModal as Modal } from 'Components';
import { TextButton as ActionButton } from 'Components/Buttons';
import { subscriptionTableStyles } from 'styles';
import { formatMoneyPlan } from 'utils';
import { subscriptionPeriodEnum } from 'utils/choices';

const getMatchingPermission = ({ tier, rowPermission }) => {
  const permission = tier.capabilities.find(
    tierPermission => tierPermission.id === rowPermission.id
  );
  return permission.enabled ? <TrueIcon /> : <FalseIcon />;
};

const SubscriptionButton = ({ plan, onClick }) => (
  <ActionButton
    label={plan && plan.cost ? formatMoneyPlan(plan.cost) : 'Free'}
    onClick={onClick}
    icon={null}
  />
);

const CancellationButton = ({ classes, label, onClick }) => (
  <ActionButton
    label={label}
    className={classes.cancel}
    onClick={onClick}
    icon={null}
  />
);

const SubscriptionTable = props => {
  const { subscriptionTier } = props.record.subscription || {};
  const { isFreeTrialUsed, id } = props.record || {};
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const dispatch = useDispatch();
  const refresh = useRefresh();
  const notify = useNotify();
  const [formData, setFormData] = useState(null);
  const [confirmModalData, setConfirmModalData] = useState({});
  const [approvalUrl, setApprovalUrl] = useState(null);
  const classes = subscriptionTableStyles();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [
    showCancelSubscriptionModal,
    setShowCancelSubscriptionModal,
  ] = useState(false);
  const firstColumnWidth = 20;
  const columnWidth =
    formData && formData.length > 0
      ? Number((100 - firstColumnWidth) / formData.length).toFixed(1)
      : 100 - firstColumnWidth;

  useEffect(() => {
    let isSubscribed = true;
    const getOrg = () => {
      dispatch(fetchStart());
      return dataProvider('GET_LIST', 'SubscriptionTier', {
        filter: {
          principalOrganisationId: id,
        },
      })
        .then(result => {
          dispatch(fetchEnd());
          if (isSubscribed) {
            setFormData(result.data);
          }
        })
        .catch(error => {
          dispatch(fetchEnd());
          const {
            extensions: { code },
            message,
          } = error.graphQLErrors[0];
          if (code !== 'INTERNAL_SERVER_ERROR') {
            notify(message, 'warning');
          }
          notify('error.subscriptionTier.unableToGetCapabilities', 'warning');
        });
    };
    getOrg();
    return () => (isSubscribed = false);
  }, [dispatch, dataProvider, setFormData, notify, id]);

  const callChangeSubscriptionMethod = () => {
    setShowConfirmModal(false);
    setShowCancelSubscriptionModal(false);
    if (
      confirmModalData.hasOwnProperty('newSubscription') &&
      confirmModalData.hasOwnProperty('orgId') &&
      confirmModalData.hasOwnProperty('planType')
    ) {
      if (!confirmModalData.newSubscription.isFreeTier) {
        setShowRedirectModal(true);
      }
      dispatch(fetchStart());

      const params = {
        organisationId: confirmModalData.orgId,
        subscriptionTierId: confirmModalData.newSubscription.id,
        period: confirmModalData.planType,
      };

      return dataProvider('CHANGE_SUBSCRIPTION', 'SubscriptionTier', {
        ...params,
      })
        .then(data => {
          dispatch(fetchEnd());
          if (data.data.approvalUrl) {
            setShowInfoModal(true);
            setApprovalUrl(data.data.approvalUrl);
            return window.open(
              data.data.approvalUrl,
              'com-pasma-paypal-subscription-payment-window'
            );
          }
          refresh();
          return notify('notification.updateSubscriptionSuccess', 'info');
        })
        .catch(error => {
          dispatch(fetchEnd());
          closeModal('redirect');
          const {
            extensions: { code },
            message,
          } = error.graphQLErrors && error.graphQLErrors[0];
          if (code !== 'INTERNAL_SERVER_ERROR') {
            return notify(message, 'warning');
          }
          return notify('error.generic', 'warning');
        });
    }
    return notify('error.generic', 'warning');
  };

  const closeModal = modal => {
    if (modal === 'redirect') setShowRedirectModal(false);
    if (modal === 'confirm') setShowConfirmModal(false);
    if (modal === 'info') setShowInfoModal(false);
    if (modal === 'cancel') setShowCancelSubscriptionModal(false);
    return;
  };

  const initialTier = formData && formData.find(tier => tier.isInitialTier);

  const confirmTextKey =
    confirmModalData.planType === subscriptionPeriodEnum.freePlan
      ? 'resources.SubscriptionTier.fields.confirm'
      : 'resources.SubscriptionTier.fields.confirmAndPay';

  return (
    <Fragment>
      {formData && formData.length > 0 ? (
        <Table>
          <colgroup>
            <col key="leftColStyle" width={`${firstColumnWidth}%`} />
            {formData.map(tier => (
              <col key={`${tier.id}-ColStyle`} width={`${columnWidth}%`} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow key={'columns'}>
              <TableCell key={'emptyPermissionHeader'}></TableCell>
              {formData.map(tier => (
                <TableCell
                  key={`${tier.id}-header`}
                  className={
                    tier.id === subscriptionTier.id
                      ? classes.currentSubscriptionTop
                      : null
                  }
                  align="center"
                >
                  {tier.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {formData[0].capabilities.map(rowPermission => (
              <TableRow key={`${rowPermission.id}-row`}>
                <TableCell
                  key={`${rowPermission.id}-cell`}
                  component="th"
                  scope="row"
                >
                  {rowPermission.description}
                </TableCell>
                {formData.map(tier => (
                  <TableCell
                    key={`${tier.id}-cell`}
                    className={
                      tier.id === subscriptionTier.id
                        ? classes.currentSubscriptionMiddle
                        : null
                    }
                    align="center"
                  >
                    {getMatchingPermission({ tier, rowPermission })}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow key={'maxExmployeesRow'}>
              <TableCell key={'maxExmployees'} component="th" scope="row">
                Maximum Employees
              </TableCell>
              {formData.map(tier => (
                <TableCell
                  key={`${tier.id}-maxExmployees`}
                  align="center"
                  className={
                    tier.id === subscriptionTier.id
                      ? classes.currentSubscriptionMiddle
                      : null
                  }
                >
                  {tier.maximumEmployees}
                </TableCell>
              ))}
            </TableRow>
            <TableRow key={'maxSubcontractorsRow'}>
              <TableCell key={'maxSubcontractors'} component="th" scope="row">
                Maximum Subcontractors
              </TableCell>
              {formData.map(tier => (
                <TableCell
                  key={`${tier.id}-maxSubcontractors`}
                  align="center"
                  className={
                    tier.id === subscriptionTier.id
                      ? classes.currentSubscriptionMiddle
                      : null
                  }
                >
                  {tier.maximumSubcontractors}
                </TableCell>
              ))}
            </TableRow>
            <TableRow key={'maxProjectsRow'}>
              <TableCell key={'maxProjects'} component="th" scope="row">
                Maximum Projects
              </TableCell>
              {formData.map(tier => (
                <TableCell
                  key={`${tier.id}-maxProjects`}
                  align="center"
                  className={
                    tier.id === subscriptionTier.id
                      ? classes.currentSubscriptionMiddle
                      : null
                  }
                >
                  {tier.maximumProjects}
                </TableCell>
              ))}
            </TableRow>
            <TableRow height={15} key={'emptyRow'}>
              <TableCell
                key={'emptyRowemptyCell'}
                component="th"
                padding={'none'}
                scope="row"
              ></TableCell>
              {formData.map(tier => (
                <TableCell
                  key={`${tier.id}-emptyRowemptyCell`}
                  padding={'none'}
                  align="right"
                ></TableCell>
              ))}
            </TableRow>
            <TableRow key={'monthlyCost'}>
              <TableCell
                key={'monthlyCostLabel'}
                className={classes.noBottomBorder}
                component="th"
                scope="row"
              >
                <em>
                  {translate(
                    'resources.SubscriptionTier.fields.monthlySubscription'
                  )}
                </em>
              </TableCell>
              {/* BOTTOM MONTHLY ROW: - Render */}
              {formData.map(tier => (
                <TableCell
                  rowSpan={
                    (tier.id === subscriptionTier.id && !tier.isInitialTier) ||
                    tier.isFreeTier
                      ? 2
                      : 1
                  }
                  className={
                    (tier.id === subscriptionTier.id && !tier.isInitialTier
                      ? classes.noBottomBorder
                      : null,
                    classes.noBottomBorder)
                  }
                  key={`${tier.id}-monthly`}
                  align="center"
                >
                  {/* Initial tier */}
                  {tier.id === subscriptionTier.id && tier.isInitialTier}

                  {/* Selected free or paid tier */}
                  {tier.id === subscriptionTier.id && !tier.isInitialTier && (
                    <CancellationButton
                      classes={classes}
                      onClick={() => {
                        setConfirmModalData({
                          currentSubscriptionId: subscriptionTier.id,
                          newSubscription: initialTier, // cancelling means switching to initial tier
                          orgId: props.id,
                          planType: subscriptionPeriodEnum.freePlan, // cancelling means swicthing to initial tier which is free
                        });
                        return setShowCancelSubscriptionModal(true);
                      }}
                      label={translate('actions.cancelSubscription')}
                      subscriptionId={subscriptionTier.id}
                      orgId={props.id}
                    />
                  )}

                  {/* Unselected paid or free */}
                  {tier.id !== subscriptionTier.id && !tier.isInitialTier && (
                    <SubscriptionButton
                      plan={tier.isFreeTier ? undefined : tier.monthlyPlan}
                      onClick={() => {
                        setConfirmModalData({
                          currentSubscriptionId: subscriptionTier.id,
                          newSubscription: tier,
                          orgId: props.id,
                          planType: tier.isFreeTier
                            ? subscriptionPeriodEnum.freePlan
                            : subscriptionPeriodEnum.monthlyPlan,
                        });
                        return setShowConfirmModal(true);
                      }}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
            <TableRow key={'annualCost'}>
              <TableCell
                key={'annualCostLabel'}
                className={classes.noBottomBorder}
                component="th"
                scope="row"
              >
                <em>
                  {translate(
                    'resources.SubscriptionTier.fields.annualSubscription'
                  )}
                </em>
              </TableCell>
              {formData.map(tier => (
                <>
                  {/* Selected initial tier -> Empty space */}
                  {/* {tier.id === subscriptionTier.id && tier.isInitialTier && (
                    <td key={`${tier.id}-freeAnnual`} />
                  )} */}

                  {/* Selected paid or free tier -> Absolutely nothing */}
                  {tier.id === subscriptionTier.id && !tier.isInitialTier}

                  {/* Unselected initial tier -> Empty space */}
                  {/* {tier.id !== subscriptionTier.id && tier.isInitialTier && (
                    <td key={`${tier.id}-freeAnnual`} />
                  )} */}

                  {/* Unselected paid or free tier -> Empty space  */}
                  {/* {tier.id !== subscriptionTier.id && !tier.isInitialTier && tier.isFreeTier && (
                    <td key={`${tier.id}-freeAnnual`} />
                  )} */}

                  {/* Unselected paid tier -> button */}
                  {tier.id !== subscriptionTier.id &&
                    !tier.isInitialTier &&
                    !tier.isFreeTier && (
                      <TableCell
                        key={`${tier.id}-annual`}
                        align="center"
                        className={classes.noBottomBorder}
                      >
                        <SubscriptionButton
                          plan={tier.annualPlan}
                          onClick={() => {
                            setConfirmModalData({
                              currentSubscriptionId: subscriptionTier.id,
                              newSubscription: tier,
                              orgId: props.id,
                              planType: subscriptionPeriodEnum.annualPlan,
                            });
                            return setShowConfirmModal(true);
                          }}
                        />
                      </TableCell>
                    )}
                </>
              ))}
            </TableRow>
            <TableRow key={'emptyRow2'}>
              <TableCell
                key={'emptyRowemptyCell2'}
                className={classes.noBottomBorder}
                component="th"
                scope="row"
              ></TableCell>
              {formData.map(tier => (
                <TableCell
                  className={classes.noBottomBorder}
                  key={`${tier.id}-emptyRowemptyCell2`}
                  align="right"
                ></TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      ) : (
        <div className={classes.centeredNotification}>
          {translate('notification.subscriptionTiersNotFound')}
        </div>
      )}
      {/* Change Subscription modal */}
      <Modal
        isOpen={showConfirmModal}
        title=""
        content={
          <ConfirmModalContent
            data={confirmModalData}
            classes={classes}
            translate={translate}
            isFreeTrialUsed={isFreeTrialUsed}
          />
        }
        confirm={confirmTextKey}
        onConfirm={() => callChangeSubscriptionMethod()}
        onClose={() => closeModal('confirm')}
      />
      {/* Cancel Subscription modal */}
      <Modal
        isOpen={showCancelSubscriptionModal}
        title=""
        content={
          <CancelSubscriptionModalContent
            data={confirmModalData}
            classes={classes}
            translate={translate}
          />
        }
        cancel="resources.SubscriptionTier.fields.closeCancelSubscription"
        onConfirm={() => callChangeSubscriptionMethod()}
        onClose={() => closeModal('cancel')}
      />
      {/* Changed subscription info modal */}
      <Modal
        isOpen={showInfoModal}
        title=""
        hideConfirm={true}
        cancel="Close"
        CancelIcon={FalseIcon}
        content={
          <InformationModalContent classes={classes} translate={translate} />
        }
        onConfirm={() => {}}
        onClose={() => closeModal('info')}
      />
      {/* You are being redirected modal */}
      <Modal
        isOpen={showRedirectModal}
        title=""
        hideConfirm={true}
        cancel="Close"
        CancelIcon={FalseIcon}
        redirectModal={true} // set the z index of the modal so it's not covered up by the info modal
        content={
          <RedirectModalContent
            approvalUrl={approvalUrl}
            classes={classes}
            translate={translate}
          />
        }
        onConfirm={() => {}}
        onClose={() => closeModal('redirect')}
      />
    </Fragment>
  );
};
export default SubscriptionTable;
